/**
 * KanbanBoard Component
 *
 * Main kanban board component that displays board statuses and cards
 * Supports auto-collapse, custom error handling, and flexible data loading
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useScreenWithOrchestrator } from '@/hooks/useScreenWithOrchestrator';
import { ScreenContainer } from '@/components/core/ScreenContainer';
import { ScreenErrorBoundary } from '@/components/core/ScreenErrorBoundary';
import { PopupErrorBoundary } from '@/components/core/PopupErrorBoundary';
import { ScreenType } from '@/types/screen.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import type {
  UpdateTaskStatusInput,
  UpdateTaskStatusOutput,
} from '@/types/generated';
import type { GetBoardUIOutput as GetBoardOutput } from '@/types/action-outputs';
import type { BoardTask } from '@/types/models/BoardTask';
import type {
  BoardStatusWithCards,
  BoardTaskWithAssignments,
  ColumnConfig,
} from '@/types/board-config.types';
import { validateCardMove } from '@/types/board-config.types';
import KanbanColumn from './KanbanColumn';
import TaskEditPopup from './TaskEditPopup';
import { getResponseCodeMessage } from '@/business/constants/response-codes';
import { useThemeColors } from '@/styles';
import { useSizing } from '@/styles/sizing';
import { layout, sizing, spacing } from '@/styles/design-tokens';
import { Alert, useAlert } from '@/components/core/Alert';
import { useOptimisticAction } from '@/hooks/useOptimisticAction';
import { useConfirmOnExit } from '@/hooks/useConfirmOnExit';
import { useConfirmOnEntry } from '@/hooks/useConfirmOnEntry';
import { Action } from '@/business/types/action.types';
import { Icon, IconName } from '@/icons';
import {
  WeekInfo,
  getWeekDates,
  isCardFrozen,
  isWeekFrozen,
} from '@/utils/week';
import { getPrioritySortOrder } from '@/utils/priority';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { BoardPreferences } from '@/types/preferences.types';
import { UserRole } from '@/types/enums/UserRole';
import { getBoardBackgroundStyle } from '@/utils/board-background';
import { logger } from '@/utils/logger';
import { useBoardQuery } from '@/hooks/queries/useBoardQuery';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { useUserMap, getUserDisplayName } from '@/hooks/queries';
import { DeletedEntityBanner } from '@/components/core/DeletedEntityBanner';
import { usePlayfulHandlers } from '@/styles/playful/usePlayfulHandlers';
import { useParticles, usePlayful } from '@/styles/playful';
import { ConfirmOnExitDialog } from './ConfirmOnExitDialog';
import { ConfirmOnEntryDialog } from './ConfirmOnEntryDialog';

// Helper to parse size from design tokens
const parseSize = (size: string | undefined): number => {
  if (!size) {
    logger.ui.warn('parseSize: received undefined size, returning 0');
    return 0;
  }
  const match = size.match(/(\d+\.?\d*)/);
  return match ? parseFloat(match[1]) : 0;
};

const FULL_COLUMN_WIDTH = parseSize(layout.kanban.columnWidth);
const COLLAPSED_COLUMN_WIDTH = parseSize(layout.kanban.columnCollapsed);
const COLUMN_GAP = parseSize(layout.kanban.columnGap);

interface KanbanBoardProps {
  /** Board ID to load (takes precedence over userId) */
  boardId?: string;
  /** User ID to load their personal board */
  userId?: string;
  /** Callback when board loads successfully - includes hasEditPermission flag and board metadata */
  onBoardLoad?: (
    boardId: string,
    boardName: string,
    hasEditPermission: boolean,
    isGroupBoard: boolean,
    ownerId: string | undefined,
    templateId: string | undefined
  ) => void;
  /** Enable auto-collapse of empty columns to prevent horizontal scroll */
  enableAutoCollapse?: boolean;
  /** Custom screen metadata overrides */
  screenMetadata?: {
    title?: string;
    showHeader?: boolean;
    showSidebar?: boolean;
  };
  /** Custom empty state */
  emptyState?: {
    title: string;
    description: string;
    icon?: React.ReactNode;
  };
  /** Enable alerts for card move errors */
  enableAlerts?: boolean;
  /** Card ID to auto-open after board loads (for deep linking) */
  initialCardId?: string;
  /** Callback when initial card modal opens (for deep linking URL cleanup) */
  onInitialCardOpened?: () => void;
  /** Selected week for filtering cards */
  selectedWeek?: WeekInfo;
  /** Board background image configuration */
  boardBackground?: BoardPreferences['background'];
}

export default function KanbanBoard({
  boardId,
  userId,
  onBoardLoad,
  enableAutoCollapse = false,
  screenMetadata,
  emptyState,
  enableAlerts = false,
  initialCardId,
  onInitialCardOpened,
  selectedWeek,
  boardBackground,
}: KanbanBoardProps) {
  const colors = useThemeColors();
  const { multiplier } = useSizing();
  const fabPlayful = usePlayfulHandlers({ interactions: ['idle'] });
  const { burst } = useParticles();
  const { reducedMotion } = usePlayful();
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const { userMap } = useUserMap(activeOrganizationId);
  const [draggedCard, setDraggedCard] =
    useState<BoardTaskWithAssignments | null>(null);
  const [newCardIds, setNewCardIds] = useState<string[]>([]);
  const boardContainerRef = useRef<HTMLDivElement>(null);
  const boardDataRef = useRef<GetBoardOutput | null>(null);
  const collapseRetryRef = useRef<NodeJS.Timeout | null>(null);

  // Board query params
  const boardQueryParams = {
    board_id: boardId,
    user_id: userId,
    dates: selectedWeek ? getWeekDates(selectedWeek) : undefined,
  };

  const {
    data: boardData,
    isLoading: isBoardLoading,
    isError: isBoardError,
    error: boardQueryError,
  } = useBoardQuery(boardQueryParams);

  // Board-LEVEL edit permission — gates board-scoped affordances (the create-card
  // FAB, board settings), NOT per-card editing. A blanket MENTOR/ADMIN role is the
  // right granularity here: these actions are not scoped to a single card's
  // assignments, so there is no backend per-card check to mirror.
  // Per-CARD edit parity with the backend's isCardAssignee/isCardMentor checks
  // lives in canEditCard below — use that for anything that acts on one card.
  // User has board-level edit permission if:
  // 1. User is the board owner, OR
  // 2. User has admin role, OR
  // 3. User has mentor role
  const hasEditPermission = useCallback(() => {
    if (!user || !boardData) return false;

    // Check if user is board owner
    if (boardData.board.owner_id && boardData.board.owner_id === user.id) {
      return true;
    }

    // Check if user is admin or mentor
    if (user.role === UserRole.ADMIN || user.role === UserRole.MENTOR) {
      return true;
    }

    return false;
  }, [user, boardData]);

  // Per-card edit scoping, mirroring the backend's isCardAssignee/isCardMentor
  // checks in update-task and update-task-status: once a card has assignments,
  // only its assignee, its specifically-assigned mentor, or an admin may edit
  // it — a mentor's blanket board role is not enough. Cards with no
  // assignments at all are unrestricted (matches backend's `len(assignments) > 0` gate).
  const canEditCard = useCallback(
    (card: BoardTaskWithAssignments | null): boolean => {
      if (!user || !card) return false;
      if (user.role === UserRole.ADMIN) return true;

      const assigneeIds = card.assignee_ids ?? [];
      const mentorIds = card.mentor_ids ?? [];
      if (assigneeIds.length === 0 && mentorIds.length === 0) return true;

      return assigneeIds.includes(user.id) || mentorIds.includes(user.id);
    },
    [user]
  );

  // Keep ref in sync with TQ data (for optimistic updates)
  useEffect(() => {
    boardDataRef.current = boardData ?? null;
  }, [boardData]);

  // Card edit popup state
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedCard, setSelectedCard] =
    useState<BoardTaskWithAssignments | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  // When the user navigates from a cohort copy to its source card, this tracks
  // whether the popup is showing a card from a different (external) board.
  const [isExternalCardView, setIsExternalCardView] = useState(false);

  // Auto-collapse state
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(
    new Set()
  );

  // Alert state
  const alert = useAlert();

  // Optimistic action for card status updates
  const { execute: executeCardMove } = useOptimisticAction<
    UpdateTaskStatusInput,
    UpdateTaskStatusOutput,
    GetBoardOutput
  >({
    action: Action.UPDATE_TASK_STATUS,
    stateRef: boardDataRef,
    setState: newState => {
      // Write optimistic state directly into TQ cache
      queryClient.setQueryData(
        queryKeys.board.detail(boardQueryParams),
        newState
      );
    },
    optimisticUpdate: (input, currentState) => {
      // Move card between columns optimistically
      const newStatuses = currentState.statuses.map(status => {
        if (status.id === input.task_id) {
          // This shouldn't happen, but handle it
          return status;
        }

        // Remove card from old column
        const filteredCards = status.cards.filter(
          card => card.id !== input.task_id
        );

        // Add card to new column
        if (status.id === input.status_id) {
          // Find the card being moved
          const movedCard = currentState.statuses
            .flatMap(s => s.cards)
            .find(c => c.id === input.task_id);

          if (movedCard) {
            return {
              ...status,
              cards: [
                ...filteredCards,
                { ...movedCard, status_id: input.status_id },
              ],
            };
          }
        }

        return { ...status, cards: filteredCards };
      });

      return { ...currentState, statuses: newStatuses };
    },
    onError: code => {
      // Show alert on error if enabled
      if (enableAlerts) {
        const errorMessage = getResponseCodeMessage(code);
        let alertType: 'error' | 'warning' = 'error';
        let alertTitle = 'Cannot Move Card';

        if (code === ResponseCode.CARD_FROZEN) {
          alertType = 'warning';
          alertTitle = 'Card Frozen';
        } else if (code === ResponseCode.IN_PROGRESS_LIMIT_EXCEEDED) {
          alertType = 'warning';
          alertTitle = 'Column Limit Reached';
        } else if (code === ResponseCode.STUDENT_BACKWARD_MOVEMENT_BLOCKED) {
          alertType = 'warning';
          alertTitle = 'Backward Movement Not Allowed';
        } else if (code === ResponseCode.COLUMN_CAPACITY_EXCEEDED) {
          alertType = 'warning';
          alertTitle = 'Column Full';
        }

        alert.show({
          type: alertType,
          title: alertTitle,
          message: errorMessage,
          buttons: [{ label: 'Got it', onClick: () => {} }],
        });
      }
    },
    onSuccess: () => {
      // Cache invalidated by UPDATE_CARD_STATUS side effect
    },
  });

  // Confirm-on-exit dialog — shared hook handles field update + card move
  const confirmOnExit = useConfirmOnExit(
    useCallback(
      async (taskId: string, statusId: string) => {
        await executeCardMove({ task_id: taskId, status_id: statusId });
      },
      [executeCardMove]
    )
  );

  // Confirm-on-entry dialog — shared hook handles field update + card move
  const confirmOnEntry = useConfirmOnEntry(
    useCallback(
      async (taskId: string, statusId: string) => {
        await executeCardMove({ task_id: taskId, status_id: statusId });
      },
      [executeCardMove]
    )
  );

  // Screen state management — TQ handles the fetch, we just drive screen state
  const screen = useScreenWithOrchestrator<GetBoardOutput>({
    metadata: {
      screenType: ScreenType.BOARD,
      title: screenMetadata?.title || boardData?.board.title || 'Kanban Board',
      requiresAuth: true,
      showHeader: screenMetadata?.showHeader ?? true,
      showSidebar: screenMetadata?.showSidebar ?? true,
    },
    onLoad: async (_strategy, screenState) => {
      // TQ drives the actual fetch; set initial loading state
      screenState.setLoading();
    },
  });

  // Drive screen state from TQ query state
  useEffect(() => {
    if (isBoardLoading) {
      screen.setLoading();
      return;
    }
    if (isBoardError) {
      const err = boardQueryError as { code?: ResponseCode } | null;
      const code = err?.code ?? ResponseCode.INTERNAL_ERROR;

      // Treat "not found" as empty state — board was deleted or doesn't exist
      if (code === ResponseCode.RESOURCE_NOT_FOUND) {
        screen.setEmpty();
        return;
      }

      screen.setError({
        code,
        message: getResponseCodeMessage(code),
        canRetry: true,
      });
      return;
    }
    if (boardData) {
      // Same board-LEVEL flag as hasEditPermission() above — consumed by
      // Dashboard to gate board settings, never to decide per-card editability.
      const canEdit =
        boardData.board.owner_id === user?.id ||
        user?.role === UserRole.ADMIN ||
        user?.role === UserRole.MENTOR;
      onBoardLoad?.(
        boardData.board.id!,
        boardData.board.title,
        canEdit,
        !!boardData.board.group_id,
        boardData.board.owner_id,
        boardData.board.template_id
      );

      if (enableAutoCollapse) {
        autoCollapseColumns(boardData);
      }

      if (
        boardData.statuses.flatMap(s => s.cards).length === 0 &&
        boardData.statuses.length === 0
      ) {
        screen.setEmpty();
      } else {
        screen.setSuccess();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBoardLoading, isBoardError, boardData]);

  // Auto-collapse logic
  const autoCollapseColumns = useCallback(
    (data: GetBoardOutput) => {
      if (!enableAutoCollapse || !boardContainerRef.current) {
        if (!boardContainerRef.current) {
          collapseRetryRef.current = setTimeout(
            () => autoCollapseColumns(data),
            50
          );
        }
        return;
      }

      // clientWidth already accounts for parent padding, so use it directly
      const availableWidth = boardContainerRef.current.clientWidth;
      const numColumns = data.statuses.length;

      // Apply sizing multiplier to all dimensions
      const actualColumnWidth = FULL_COLUMN_WIDTH * multiplier;
      const actualCollapsedWidth = COLLAPSED_COLUMN_WIDTH * multiplier;
      const actualGap = COLUMN_GAP * multiplier;

      const totalGaps = (numColumns - 1) * actualGap;
      const requiredWidthFull = numColumns * actualColumnWidth + totalGaps;
      const MINIMAL_BUFFER = 50;

      // If everything fits, don't collapse
      if (requiredWidthFull <= availableWidth - MINIMAL_BUFFER) {
        setCollapsedColumns(new Set());
        return;
      }

      // Find empty columns to collapse
      const emptyColumns: BoardStatusWithCards[] = [];
      data.statuses.forEach((status: BoardStatusWithCards) => {
        if (status.cards.length === 0) {
          emptyColumns.push(status);
        }
      });

      if (emptyColumns.length === 0) {
        setCollapsedColumns(new Set());
        return;
      }

      // Sort empty columns by position (right to left)
      emptyColumns.sort((a, b) => b.position - a.position);

      // Collapse from right to left until everything fits
      const toCollapse = new Set<string>();
      let currentWidth = requiredWidthFull;

      for (const column of emptyColumns) {
        if (currentWidth <= availableWidth) break;
        toCollapse.add(column.id!);
        currentWidth -= actualColumnWidth - actualCollapsedWidth;
      }

      setCollapsedColumns(toCollapse);
    },
    [enableAutoCollapse, multiplier]
  );

  const toggleColumn = (columnId: string) => {
    setCollapsedColumns(prev => {
      const updated = new Set(prev);
      if (updated.has(columnId)) {
        updated.delete(columnId);
      } else {
        updated.add(columnId);
      }
      return updated;
    });
  };

  // Re-calculate auto-collapse on window resize
  useEffect(() => {
    if (!boardData || !enableAutoCollapse) return;

    autoCollapseColumns(boardData);
    const timeoutId = setTimeout(() => autoCollapseColumns(boardData), 50);

    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => autoCollapseColumns(boardData), 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimer);
      if (collapseRetryRef.current) clearTimeout(collapseRetryRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [boardData, autoCollapseColumns, enableAutoCollapse]);

  // Cleanup collapse retry timeout on unmount
  useEffect(() => {
    return () => {
      if (collapseRetryRef.current) clearTimeout(collapseRetryRef.current);
    };
  }, []);

  // Auto-open card when initialCardId is set (for deep linking)
  useEffect(() => {
    if (!initialCardId || !boardData) return;

    // Find the card in board data
    const card = boardData.statuses
      .flatMap(status => status.cards)
      .find(c => c.id === initialCardId);

    if (card) {
      // Open the card modal
      setSelectedCard(card);
      setIsCreateMode(false);
      setIsEditPopupOpen(true);

      // Notify parent that card modal opened
      onInitialCardOpened?.();

      logger.debug('[KanbanBoard] Auto-opened card for deep link:', {
        task_id: initialCardId,
      });
    } else {
      logger.warn('[KanbanBoard] Card not found for deep link:', {
        task_id: initialCardId,
      });
      // Still notify parent so URL gets cleaned
      onInitialCardOpened?.();
    }
  }, [initialCardId, boardData, onInitialCardOpened]);

  const handleCardDragStart = (
    e: React.DragEvent,
    card: BoardTaskWithAssignments
  ) => {
    // Prevent dragging on deleted boards
    if (boardData?.board.deleted_at) {
      e.preventDefault();
      return;
    }
    // Prevent dragging frozen cards (defense-in-depth alongside draggable={false})
    if (isCardFrozen(card.start_date, user?.role)) {
      e.preventDefault();
      return;
    }
    // Prevent dragging cards the user isn't scoped to move, mirroring the
    // backend's isCardAssignee/isCardMentor check (defense-in-depth
    // alongside draggable={false} in KanbanCard).
    if (!canEditCard(card)) {
      e.preventDefault();
      return;
    }
    setDraggedCard(card);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCardDragEnd = () => {
    setDraggedCard(null);
  };

  const handleCardClick = (card: BoardTaskWithAssignments) => {
    setSelectedCard(card);
    setIsCreateMode(false);
    setIsEditPopupOpen(true);
  };

  const handleCreateCardClick = () => {
    setSelectedCard(null);
    setIsCreateMode(true);
    setIsEditPopupOpen(true);
  };

  const handleCardUpdated = () => {
    // Side effects invalidate by board_id, but when the board was loaded
    // via user_id the cached query key has board_id: undefined → no match.
    // Invalidate with the exact params used by useBoardQuery so the cache hit is guaranteed.
    queryClient.invalidateQueries({
      queryKey: queryKeys.board.detail(boardQueryParams),
    });
  };

  const handleSubcardsCreated = (newCards: BoardTask[]) => {
    if (newCards.length === 0) return;
    const ids = newCards.map(c => c.id!);
    setNewCardIds(ids);
    setTimeout(() => setNewCardIds([]), 2000);
    // Patch TQ cache directly with new cards — no full reload needed
    queryClient.setQueryData<GetBoardOutput>(
      queryKeys.board.detail(boardQueryParams),
      prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          statuses: prev.statuses.map(status => {
            const incoming = newCards.filter(c => c.status_id === status.id);
            if (incoming.length === 0) return status;
            return { ...status, cards: [...status.cards, ...incoming] };
          }),
        };
        boardDataRef.current = updated;
        return updated;
      }
    );
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = async (
    e: React.DragEvent,
    targetStatusId: string
  ) => {
    e.preventDefault();

    if (!draggedCard) return;

    // Only proceed if the card is being moved to a different status
    if (draggedCard.status_id === targetStatusId) {
      setDraggedCard(null);
      return;
    }

    // Defense-in-depth: re-check scoping at drop time in case draggedCard
    // state went stale between dragstart and drop (e.g. assignments changed
    // mid-drag). The backend enforces this regardless; this just avoids the
    // optimistic-update-then-rollback UX for a move we already know is denied.
    if (!canEditCard(draggedCard)) {
      setDraggedCard(null);
      return;
    }

    // Validate move before sending to backend
    if (boardData && user) {
      const sourceColumn = boardData.statuses.find(
        s => s.id === draggedCard.status_id
      );
      const targetColumn = boardData.statuses.find(
        s => s.id === targetStatusId
      );

      if (sourceColumn && targetColumn) {
        const validation = validateCardMove(
          sourceColumn,
          targetColumn,
          user.role as UserRole,
          draggedCard
        );

        if (!validation.valid) {
          // Intercept confirmOnExit — show dialog instead of error
          if (
            validation.errorCode === 'CONFIRM_ON_EXIT_REQUIRED' &&
            validation.confirmOnExit
          ) {
            confirmOnExit.trigger(
              draggedCard,
              targetStatusId,
              validation.confirmOnExit
            );
            setDraggedCard(null);
            return;
          }

          // Intercept confirmOnEntry — show dialog instead of error
          if (
            validation.errorCode === 'CONFIRM_ON_ENTRY_REQUIRED' &&
            validation.confirmOnEntry
          ) {
            confirmOnEntry.trigger(
              draggedCard,
              targetStatusId,
              validation.confirmOnEntry
            );
            setDraggedCard(null);
            return;
          }

          if (enableAlerts) {
            let alertTitle = 'Cannot Move Card';
            if (validation.errorCode === 'CAPACITY_EXCEEDED') {
              alertTitle = 'Column Full';
            } else if (
              validation.errorCode === 'STUDENT_BACKWARD_MOVEMENT_BLOCKED'
            ) {
              alertTitle = 'Backward Movement Not Allowed';
            }

            alert.show({
              type: 'warning',
              title: alertTitle,
              message: validation.error || 'This move is not allowed',
              buttons: [{ label: 'Got it', onClick: () => {} }],
            });
          }
          setDraggedCard(null);
          return;
        }
      }
    }

    // Detect if this drop lands on the terminal (done) column
    const isCompletionMove =
      !reducedMotion &&
      !!boardData &&
      boardData.statuses.length > 0 &&
      (() => {
        const maxPos = Math.max(...boardData.statuses.map(s => s.position));
        return (
          boardData.statuses.find(s => s.id === targetStatusId)?.position ===
          maxPos
        );
      })();

    const taskId = draggedCard.id!;

    // Start optimistic update (fires synchronously inside execute, then awaits server)
    const movePromise = executeCardMove({
      task_id: taskId,
      status_id: targetStatusId,
    });

    if (isCompletionMove) {
      // Particles burst at the drop point immediately
      burst(e.clientX, e.clientY, 35);
      // All mascots celebrate in place
      window.dispatchEvent(new CustomEvent('card-completed'));
      // Animate the card after React re-renders it into the new column
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const cardEl = document.querySelector(`[data-card-id="${taskId}"]`);
          if (cardEl instanceof HTMLElement) {
            cardEl.classList.add('animate-card-complete');
            cardEl.addEventListener(
              'animationend',
              () => cardEl.classList.remove('animate-card-complete'),
              { once: true }
            );
          }
        });
      });
    }

    await movePromise;
    setDraggedCard(null);
  };

  const defaultEmptyState = {
    title: 'No cards for this week',
    description:
      'There are no cards assigned to this week. Your mentor may sync some soon, or you can create cards if you have permission.',
    icon: (
      <svg
        className="w-10 h-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: colors.text.tertiary }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
  };

  return (
    <>
      <ScreenErrorBoundary>
        <ScreenContainer
          state={screen.state}
          error={screen.error}
          onRetry={screen.retry}
          skeleton={{ type: 'kanban' }}
          emptyState={emptyState || defaultEmptyState}
        >
          {boardData && (
            <div
              className="h-full flex flex-col px-3 sm:px-6 py-4 relative"
              style={{
                ...getBoardBackgroundStyle(boardBackground),
                backgroundColor: colors.background, // Fallback when no background
                ...(boardData.board.deleted_at ? { opacity: 0.7 } : {}),
              }}
            >
              {/* Deleted board banner */}
              {boardData.board.deleted_at && (
                <DeletedEntityBanner
                  entityType="board"
                  deletedAt={boardData.board.deleted_at as string}
                  restoreAction={Action.RESTORE_BOARD}
                  restoreInput={{ board_id: boardData.board.id }}
                  canRestore={user?.role === UserRole.ADMIN}
                  onRestored={() => {
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.board.all(),
                    });
                  }}
                />
              )}
              {/* Floating Create Card Button */}
              {hasEditPermission() &&
                !boardData.board.deleted_at &&
                !isWeekFrozen(selectedWeek, user?.role) && (
                  <button
                    onClick={handleCreateCardClick}
                    className="fixed rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50"
                    style={{
                      bottom: `max(${spacing.md}, env(safe-area-inset-bottom, 0px))`,
                      right: spacing.xl,
                      width: sizing.button.xl,
                      height: sizing.button.xl,
                      backgroundColor: colors.blue,
                      color: colors.text.inverse,
                      ...fabPlayful.style,
                    }}
                    title="Create new card"
                  >
                    <Icon
                      name={IconName.PLUS}
                      size="xl"
                      color={colors.text.inverse}
                    />
                  </button>
                )}

              {/* Kanban Columns */}
              <div
                ref={boardContainerRef}
                className="flex-1 overflow-x-auto overflow-y-hidden min-h-0 py-2"
              >
                <div className="inline-flex gap-4 h-full items-stretch">
                  {boardData.statuses.map(status => {
                    const isCollapsed =
                      enableAutoCollapse && collapsedColumns.has(status.id!);

                    // Sort cards by priority (high -> medium -> low) then by created_at
                    // Note: Week filtering is done on the backend via dates parameter
                    const sortedCards = [...status.cards].sort((a, b) => {
                      // Extract priority from metadata (default to 'low' if not set)
                      const priorityA = (a.metadata as { priority?: string })
                        ?.priority;
                      const priorityB = (b.metadata as { priority?: string })
                        ?.priority;

                      const orderA = getPrioritySortOrder(priorityA);
                      const orderB = getPrioritySortOrder(priorityB);

                      // Sort by priority first
                      if (orderA !== orderB) {
                        return orderA - orderB;
                      }

                      // For same priority, sort by created_at (oldest first)
                      const dateA = new Date(a.created_at || 0).getTime();
                      const dateB = new Date(b.created_at || 0).getTime();
                      return dateA - dateB;
                    });

                    return (
                      <KanbanColumn
                        key={status.id}
                        id={status.id!}
                        name={status.name}
                        color={status.color}
                        cards={sortedCards}
                        newCardIds={newCardIds}
                        isCollapsed={isCollapsed}
                        onToggleCollapse={
                          enableAutoCollapse
                            ? () => toggleColumn(status.id!)
                            : undefined
                        }
                        onCardDragStart={handleCardDragStart}
                        onCardDragEnd={handleCardDragEnd}
                        onDrop={handleColumnDrop}
                        onDragOver={handleColumnDragOver}
                        onCardClick={handleCardClick}
                        userRole={user?.role}
                        canEditCard={canEditCard}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Owner signature — overlaid at bottom center, no layout impact */}
              {boardData.board.owner_id &&
                (() => {
                  const ownerName = getUserDisplayName(
                    userMap,
                    boardData.board.owner_id
                  );
                  if (!ownerName) return null;
                  const profileUrl = generateDeepLink(
                    DeepLinkEntity.USER,
                    boardData.board.owner_id
                  );
                  return (
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontFamily:
                          "'Dancing Script', 'Brush Script MT', 'Segoe Script', cursive",
                        fontStyle: 'italic',
                        fontSize: 'calc(1.15rem * var(--sizing-multiplier, 1))',
                        color: colors.text.tertiary,
                        textDecoration: 'none',
                        letterSpacing: '0.04em',
                        opacity: 0.5,
                        transition: 'opacity 0.2s, color 0.2s',
                        pointerEvents: 'auto',
                        zIndex: 40,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.color = colors.text.secondary;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.opacity = '0.5';
                        e.currentTarget.style.color = colors.text.tertiary;
                      }}
                    >
                      {ownerName}
                    </a>
                  );
                })()}
            </div>
          )}
        </ScreenContainer>
      </ScreenErrorBoundary>

      {/* Card Edit/Create Popup */}
      <PopupErrorBoundary
        isOpen={isEditPopupOpen}
        onClose={() => {
          setIsEditPopupOpen(false);
          setIsExternalCardView(false);
        }}
      >
        <TaskEditPopup
          isOpen={isEditPopupOpen}
          onClose={() => {
            setIsEditPopupOpen(false);
            setIsExternalCardView(false);
          }}
          card={selectedCard}
          statuses={boardData?.statuses || []}
          onCardUpdated={handleCardUpdated}
          onSubcardsCreated={handleSubcardsCreated}
          organizationId={boardData?.board.organization_id || ''}
          boardId={
            isExternalCardView ? selectedCard?.board_id : boardData?.board.id
          }
          isCreateMode={isCreateMode}
          isFrozen={
            isExternalCardView ||
            (!isCreateMode &&
              isCardFrozen(selectedCard?.start_date, user?.role))
          }
          isEditForbidden={
            !isCreateMode && !isExternalCardView && !canEditCard(selectedCard)
          }
          isStartDateDisabled={!isCreateMode && user?.role !== UserRole.ADMIN}
          isAdmin={!isExternalCardView && user?.role === UserRole.ADMIN}
          isEditLocked={
            !isCreateMode &&
            !isExternalCardView &&
            user?.role !== UserRole.ADMIN &&
            !!(
              boardData?.statuses.find(s => s.id === selectedCard?.status_id)
                ?.config as ColumnConfig | undefined
            )?.lockEdits
          }
          boardTag={isExternalCardView ? undefined : boardData?.board.tag}
          onNavigateToCard={navCard => {
            setSelectedCard(navCard);
            setIsExternalCardView(true);
          }}
        />
      </PopupErrorBoundary>

      {/* Alert Dialog (when enabled) */}
      {enableAlerts && <Alert {...alert.props} />}

      {/* Confirm on Exit Dialog */}
      {confirmOnExit.state && (
        <ConfirmOnExitDialog
          open={!!confirmOnExit.state}
          onOpenChange={open => {
            if (!open) confirmOnExit.dismiss();
          }}
          card={confirmOnExit.state.card}
          fieldConfig={confirmOnExit.state.fieldConfig}
          onConfirm={confirmOnExit.handleSubmit}
          isSubmitting={confirmOnExit.isSubmitting}
        />
      )}
      <Alert {...confirmOnExit.alertProps} />

      {/* Confirm on Entry Dialog */}
      {confirmOnEntry.state && (
        <ConfirmOnEntryDialog
          open={!!confirmOnEntry.state}
          onOpenChange={open => {
            if (!open) confirmOnEntry.dismiss();
          }}
          card={confirmOnEntry.state.card}
          fieldConfig={confirmOnEntry.state.fieldConfig}
          onConfirm={confirmOnEntry.handleSubmit}
          isSubmitting={confirmOnEntry.isSubmitting}
        />
      )}
      <Alert {...confirmOnEntry.alertProps} />
    </>
  );
}
